import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "@/components/base/select/select";
import { Button } from "@/components/base/buttons/button";
import {
    useTrackingCarriers,
    useSetContainerCarrier,
} from "@/hooks/api/use-shipments";
import { toast } from "@/lib/toast-service";

interface ManualCarrierSelectProps {
    containerId: string;
    onDone?: () => void;
}

/**
 * Small carrier picker shown when JSONCargo tracking auto-detection fails.
 * The user selects one of the supported shipping lines; on confirm we persist it
 * and re-run the sync.
 */
export function ManualCarrierSelect({ containerId, onDone }: ManualCarrierSelectProps) {
    const { t } = useTranslation("pages:containers");
    const { data: carriers = [] } = useTrackingCarriers();
    const setCarrier = useSetContainerCarrier();
    const [value, setValue] = useState<string | null>(null);

    const handleConfirm = () => {
        if (!value) return;
        setCarrier.mutate(
            { containerId, shippingLine: value },
            {
                onSuccess: (res) => {
                    if (res?.error) {
                        toast.error(
                            `${t("detail.tracking.carrierSetError", "Could not update tracking")}: ${res.error}`,
                        );
                    } else {
                        toast.success(
                            t("detail.tracking.carrierSetSuccess", "Carrier set — tracking refreshed"),
                        );
                    }
                    onDone?.();
                },
                onError: () => {
                    toast.error(t("detail.tracking.carrierSetError", "Could not update tracking"));
                },
            },
        );
    };

    return (
        <div className="flex items-end gap-2">
            <Select
                aria-label={t("detail.tracking.setCarrierManually", "Set the carrier manually")}
                placeholder={t("detail.tracking.carrierSelectPlaceholder", "Select carrier…")}
                size="sm"
                items={carriers.map((c) => ({ id: c.value, label: c.label }))}
                selectedKey={value}
                onSelectionChange={(key) => setValue(key as string)}
                isDisabled={setCarrier.isPending}
            >
                {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
            </Select>
            <Button
                size="sm"
                color="primary"
                onClick={handleConfirm}
                isDisabled={!value}
                isLoading={setCarrier.isPending}
            >
                {t("detail.tracking.setCarrierConfirm", "Set carrier")}
            </Button>
        </div>
    );
}
